import { isEmpty } from 'lodash';
import {
  CASINO_ACTION_TYPE,
  PAY_AND_PLAY_DEPOSIT_FAILED_REASON,
  SESSION_REUNLOCK_REASON,
} from '../data-types/initialisation/initialisationEnums';
import { SocketMessage } from '../data-types/initialisation/initialisationInterfaces';
import { DialogType } from '../data-types/notification/notificationEnums';
import { NotificationPayload } from '../data-types/notification/notificationInterfaces';
import { LimitConfirmation } from '../data-types/profile/profile-sections/staying-in-control/stayingInControlInterfaces';
import { Ticket } from '../data-types/sports/sportsInterfaces';
import { SuperchargedBetInfo } from '../data-types/supercharged-bet/superchargedBetInterfaces';
import { VALIDATION_TYPE } from '../data-types/validation/validationEnums';
import {
  bonusWalletUpdate,
  cashoutAccepted,
  cashoutOfferUpdate,
  cashoutRejected,
  casinoAction,
  dialogOpen,
  eventUpdate,
  limitUpdated,
  obsMoodbarScoreUpdate,
  paymentsDepositFailure,
  sessionLogout,
  sessionReUnlock,
  ticketAccepted,
  ticketAcceptedFreeTicket,
  ticketBoost,
  ticketCancelReason,
  ticketFeaturedsupercharge,
  ticketRejected,
  userDetailsWeb,
  userInAppNotification,
  userRealityCheck,
  walletUpdate,
  zimplerDepositFailed,
} from '../socket-types/broadcastSocketTypes';
import { enqueueSuperchargedBet } from '../state/actions/betActions';
import { setMoodBarScore } from '../state/actions/customizationActions';
import { decreaseLimitConfirmationDialogCount, enqueueDialog, increaseLimitConfirmationDialogCount } from '../state/actions/dialogActions';
import {
  handleBonusWalletRemoval,
  handleBonusWalletUpdateBroadcast,
  handleCashoutAcceptedBroadcast,
  handleCashoutOfferUpdateBroadcast,
  handleCashoutRejectedBroadcast,
  handleCasinoActionBroadcast,
  handleEventBroadcast,
  handleInAppNotification,
  handleNewUserDetails,
  handlePaymentDepositFailedBroadcast,
  handleSessionLogoutBroadcast,
  handleSessionReUnlockBroadcast,
  handleTicketAcceptedBroadcast,
  handleTicketAcceptedFreeTicketBroadcast,
  handleTicketBoostBroadcast,
  handleTicketCancelReasonBroadcast,
  handleTicketRejectedBroadcast,
  handleUserRealityCheckBroadcast,
  handleWalletUpdateBroadcast,
  handleZimplerDepositFailedBroadcast,
} from '../utils/broadcast';
import { tryToGenerateCountrySpecificUserData } from '../utils/superchargedBets';
import { isOfType } from '../validation/typeValidation';

export const handleSocketBroadcasts = (data: SocketMessage) => {
  switch (data.message) {
    case walletUpdate: {
      if (data.payload.balance !== undefined && data.payload.sourceId !== undefined) {
        handleWalletUpdateBroadcast(data.payload.balance, data.payload.sourceId, data.payload.transactionId);
      }
      break;
    }
    case ticketAccepted: {
      if (data.payload.ticket) {
        handleTicketAcceptedBroadcast(data.payload.ticket as Ticket);
      }
      break;
    }
    case ticketBoost: {
      if (data.payload.ticket) {
        handleTicketBoostBroadcast(data.payload.ticket as Ticket);
      }
      break;
    }
    case ticketAcceptedFreeTicket: {
      if (data.payload.ticket && data.payload.referrer) {
        handleTicketAcceptedFreeTicketBroadcast(data.payload.ticket as Ticket, data.payload.referrer);
      }
      break;
    }
    case ticketRejected: {
      if (data.payload.error && data.payload.ticket) {
        handleTicketRejectedBroadcast(data.payload.ticket as Ticket, data.payload.error);
      }
      break;
    }
    case ticketCancelReason: {
      if (data.payload.message && data.payload.ticket) {
        handleTicketCancelReasonBroadcast(data.payload.ticket as Ticket, data.payload.message);
      }
      break;
    }
    case userRealityCheck: {
      if (data.payload.realityCheckInterval) {
        handleUserRealityCheckBroadcast(data.payload.realityCheckInterval);
      }
      break;
    }
    case sessionLogout: {
      handleSessionLogoutBroadcast();
      break;
    }
    case eventUpdate: {
      if (data.payload.event || data.payload.race) {
        handleEventBroadcast(data);
      }
      break;
    }
    case cashoutOfferUpdate: {
      if (
        data.payload.ticketId &&
        (data.payload.netCashOutOffer === null || isOfType(data.payload.netCashOutOffer, VALIDATION_TYPE.NUMBER))
      ) {
        handleCashoutOfferUpdateBroadcast(data.payload.ticketId, Number(data.payload.netCashOutOffer));
      }
      break;
    }
    case cashoutAccepted: {
      if (
        isOfType(data.payload.netCashOutOffer, VALIDATION_TYPE.NUMBER) &&
        isOfType(data.payload.ticketId, VALIDATION_TYPE.NUMBER) &&
        isOfType(data.payload.stake, VALIDATION_TYPE.NUMBER)
      ) {
        handleCashoutAcceptedBroadcast(
          Number(data.payload.ticketId),
          Number(data.payload.netCashOutOffer),
          Number(data.payload.stake),
          data.payload.netBoostedCashoutOffer,
        );
      }
      break;
    }
    case cashoutRejected: {
      if (isOfType(data.payload.ticketId, VALIDATION_TYPE.NUMBER) && data.payload.error) {
        handleCashoutRejectedBroadcast(Number(data.payload.ticketId), data.payload.cashoutOffer, data.payload.error);
      }
      break;
    }
    case userInAppNotification: {
      handleInAppNotification(data.payload as NotificationPayload);
      break;
    }
    case obsMoodbarScoreUpdate: {
      setMoodBarScore(data.payload.score || 0);
      break;
    }
    case dialogOpen: {
      const content = data.payload as LimitConfirmation;
      enqueueDialog({ content, type: DialogType.LIMIT_CONFIRMATION_DIALOG });
      increaseLimitConfirmationDialogCount();
      break;
    }
    case limitUpdated: {
      decreaseLimitConfirmationDialogCount();
      break;
    }
    case userDetailsWeb: {
      if (data.payload && data.payload.data) {
        handleNewUserDetails(data.payload.data);
      }
      break;
    }
    case casinoAction: {
      if (data.payload && data.payload.amount && data.payload.type) {
        handleCasinoActionBroadcast(data.payload.amount, data.payload.type as CASINO_ACTION_TYPE);
      }
      break;
    }
    case paymentsDepositFailure: {
      if (data.payload && isOfType(data.payload.status, VALIDATION_TYPE.NUMBER)) {
        handlePaymentDepositFailedBroadcast(Number(data.payload.status));
      }
      break;
    }
    case sessionReUnlock: {
      if (
        data.payload &&
        data.payload.sessionToken &&
        (data.payload.reason === SESSION_REUNLOCK_REASON.PAY_AND_PLAY_DEPOSIT_SUCCESS ||
          data.payload.reason === SESSION_REUNLOCK_REASON.PAY_AND_PLAY_DEPOSIT_FAILED_AND_KYC_SUCCESS)
      ) {
        handleSessionReUnlockBroadcast(data.payload.sessionToken);
      }
      break;
    }
    case zimplerDepositFailed: {
      if (data.payload && data.payload.reason === PAY_AND_PLAY_DEPOSIT_FAILED_REASON.PAY_AND_PLAY_DEPOSIT_FAILED) {
        handleZimplerDepositFailedBroadcast();
      }
      break;
    }
    case ticketFeaturedsupercharge: {
      const { user, ticket, singleBetDetails } = data.payload;
      if (user && ticket) {
        enqueueSuperchargedBet({ ticket, singleBetDetails, user: tryToGenerateCountrySpecificUserData(user) } as SuperchargedBetInfo);
      }
      break;
    }

    case bonusWalletUpdate: {
      const { bonusWallet } = data.payload;

      if (!!bonusWallet && !isEmpty(bonusWallet)) {
        handleBonusWalletUpdateBroadcast(bonusWallet);
      } else if (bonusWallet === null) {
        handleBonusWalletRemoval();
      }

      break;
    }
    default:
      break;
  }
};
